import { View, ViewDensity } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { ViewTableCheckboxCell } from "@design-system/ViewTable/ViewTableCheckboxCell";
import { NestedEditorRow } from "./nestedRowTypes";
import { NestedNameCell } from "./cells/NestedNameCell";
import { NestedCodeCell } from "./cells/NestedCodeCell";
import { NestedSourceCell } from "./cells/NestedSourceCell";
import { NestedForm1099BoxCell } from "./cells/NestedForm1099BoxCell";
import { NestedEntitiesCell } from "./cells/NestedEntitiesCell";
import { NestedActionsCell } from "./cells/NestedActionsCell";
import { NestedDragHandleCell } from "./cells/NestedDragHandleCell";

export enum ChartOfAccountsTemplateNestedColumn {
    SELECT = "select",
    DRAG = "drag",
    NAME = "name",
    CODE = "code",
    SOURCE = "source",
    FORM_1099_BOX = "form1099Box",
    ENTITIES = "entities",
    ACTIONS = "actions",
}

// The nested editor is read-only and has no header sort, so the sort type is
// `never` (mirrors the accounts tree view config).
export const chartOfAccountsTemplateNestedViewConfig: ViewConfig<
    NestedEditorRow,
    ChartOfAccountsTemplateNestedColumn,
    never
> = {
    columns: {
        // Custom selection column. We opt out of the framework's
        // auto-injected select column (`bulkActions: false`) and own selection
        // via `enableRowSelection` + a controlled `rowSelection`. Only
        // account + group rows return `getCanSelect()`, so structural section /
        // built-in tier rows render no checkbox. No header select-all checkbox:
        // every top-level row is a non-selectable section and sub-row cascade is
        // off, so a header toggle would select nothing — "Select all" lives in
        // the command island (which counts the selectable nested rows) instead.
        [ChartOfAccountsTemplateNestedColumn.SELECT]: {
            accessorKey: "id",
            name: "Select",
            header: "",
            cell: ({ row, table }) =>
                row.getCanSelect() ? (
                    <ViewTableCheckboxCell row={row} table={table} />
                ) : null,
            minSize: 40,
            maxSize: 40,
            immutable: true,
            internal: true,
            hiddenFromSettings: true,
        },
        // Drag handle (six-dot) — only account + custom group rows render one
        // (see NestedDragHandleCell). DnD is wired via ViewTable's
        // `rowDragAndDrop`, which feeds the handle attributes/listeners into
        // this column.
        [ChartOfAccountsTemplateNestedColumn.DRAG]: {
            accessorKey: "id",
            name: "",
            header: "",
            cell: NestedDragHandleCell,
            minSize: 36,
            maxSize: 36,
            immutable: true,
            internal: true,
            hiddenFromSettings: true,
        },
        [ChartOfAccountsTemplateNestedColumn.NAME]: {
            accessorKey: "name",
            name: "Account name",
            header: "Account name",
            cell: NestedNameCell,
            immutable: true,
            minSize: 320,
            maxSize: 1200,
        },
        [ChartOfAccountsTemplateNestedColumn.CODE]: {
            accessorKey: "id",
            name: "Code",
            header: "Code",
            cell: NestedCodeCell,
            minSize: 100,
            maxSize: 160,
        },
        [ChartOfAccountsTemplateNestedColumn.SOURCE]: {
            accessorKey: "id",
            name: "Source",
            header: "Source",
            cell: NestedSourceCell,
            minSize: 110,
            maxSize: 160,
        },
        [ChartOfAccountsTemplateNestedColumn.FORM_1099_BOX]: {
            accessorKey: "id",
            name: "1099 box",
            header: "1099 box",
            cell: NestedForm1099BoxCell,
            minSize: 130,
            maxSize: 200,
        },
        // Sized to match the chart page's own Entities column.
        [ChartOfAccountsTemplateNestedColumn.ENTITIES]: {
            accessorKey: "id",
            name: "Entities",
            header: "Entities",
            cell: NestedEntitiesCell,
            minSize: 100,
            maxSize: 100,
        },
        [ChartOfAccountsTemplateNestedColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: NestedActionsCell,
            // Wide enough to fit the full hover-revealed cluster ("Add group" +
            // "Add account" secondary buttons + the ⋯ menu) without clipping the
            // left edge. The cell clips overflow, so the column itself must hold
            // the whole right-aligned cluster.
            minSize: 300,
            maxSize: 300,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: ChartOfAccountsTemplateNestedColumn.NAME,
        childrenKey: "children",
    },
};

// `showEntities` suits an entity-scoped host only: a template holds one
// synthetic entity, so the column would say nothing. `showForm1099Box` is
// template-only: the workspace chart edits the box on the chart page.
export const buildChartOfAccountsTemplateNestedView = ({
    showEntities,
    showCode,
    showForm1099Box,
}: {
    showEntities: boolean;
    showCode: boolean;
    showForm1099Box: boolean;
}): View => ({
    columns: [
        {
            id: ChartOfAccountsTemplateNestedColumn.SELECT,
            options: { pin: "left" as const },
        },
        {
            id: ChartOfAccountsTemplateNestedColumn.DRAG,
            options: { pin: "left" as const },
        },
        {
            id: ChartOfAccountsTemplateNestedColumn.NAME,
            options: { pin: "left" as const },
        },
        ...(showCode ? [{ id: ChartOfAccountsTemplateNestedColumn.CODE }] : []),
        { id: ChartOfAccountsTemplateNestedColumn.SOURCE },
        ...(showForm1099Box
            ? [{ id: ChartOfAccountsTemplateNestedColumn.FORM_1099_BOX }]
            : []),
        ...(showEntities
            ? [{ id: ChartOfAccountsTemplateNestedColumn.ENTITIES }]
            : []),
        {
            id: ChartOfAccountsTemplateNestedColumn.ACTIONS,
            options: { pin: "right" as const },
        },
    ],
    filters: {},
    options: {
        selection: true,
        // Our own select column (see `columns.select`) — the framework must not
        // auto-inject one. Selection still works via the explicit
        // `enableRowSelection` + controlled `rowSelection` passed to ViewTable.
        bulkActions: false,
        density: ViewDensity.MD,
    },
});
